<script setup>
import { ref } from 'vue'
import { IconCheckCircleFill, IconCloseCircleFill, IconExclamationCircleFill, IconInfoCircleFill, IconClose } from '@arco-design/web-vue/es/icon'

const messages = ref([])

// 消息类型配置
const messageConfig = {
  success: {
    icon: IconCheckCircleFill,
    color: '#52c41a',
    bgColor: 'rgba(82, 196, 26, 0.1)'
  },
  error: {
    icon: IconCloseCircleFill,
    color: '#ff4d4f',
    bgColor: 'rgba(255, 77, 79, 0.1)'
  },
  warning: {
    icon: IconExclamationCircleFill,
    color: '#faad14',
    bgColor: 'rgba(250, 173, 20, 0.1)'
  },
  info: {
    icon: IconInfoCircleFill,
    color: '#1890ff',
    bgColor: 'rgba(24, 144, 255, 0.1)'
  }
}

// 添加消息
const addMessage = (message) => {
  const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
  const newMessage = {
    id,
    duration: 3000,
    closable: true,
    showIcon: true,
    ...message
  }
  
  messages.value.push(newMessage)
  
  // 自动关闭
  if (newMessage.duration && newMessage.duration > 0) {
    setTimeout(() => {
      removeMessage(id)
    }, newMessage.duration)
  }
  
  return id
}

// 移除消息
const removeMessage = (id) => {
  const index = messages.value.findIndex(msg => msg.id === id)
  if (index > -1) {
    messages.value.splice(index, 1)
  }
}

// 清空所有消息
const clearAll = () => {
  messages.value = []
}

// 暴露方法给外部使用
defineExpose({
  addMessage,
  removeMessage,
  clearAll,
  success: (title, content, options) => 
    addMessage({ type: 'success', title, content, ...options }),
  error: (title, content, options) => 
    addMessage({ type: 'error', title, content, ...options }),
  warning: (title, content, options) => 
    addMessage({ type: 'warning', title, content, ...options }),
  info: (title, content, options) => 
    addMessage({ type: 'info', title, content, ...options })
})
</script>

<template>
  <div class="zl-message-container">
    <transition-group name="message" tag="div">
      <div
        v-for="message in messages"
        :key="message.id"
        class="zl-message-item"
        :class="`zl-message-${message.type}`"
      >
        <div class="message-content">
          <div v-if="message.showIcon" class="message-icon">
            <component 
              :is="messageConfig[message.type].icon" 
              :style="{ color: messageConfig[message.type].color }"
            />
          </div>
          <div class="message-text">
            <div class="message-title">{{ message.title }}</div>
            <div v-if="message.content" class="message-description">{{ message.content }}</div>
          </div>
        </div>
        <div v-if="message.closable" class="message-close" @click="removeMessage(message.id)">
          <IconClose />
        </div>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.zl-message-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.zl-message-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 320px;
  max-width: 480px;
  margin-bottom: 12px;
  padding: 16px 20px;
  pointer-events: auto;
  
  /* 毛玻璃效果 */
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  
  /* 阴影效果 */
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 10px 30px rgba(0, 0, 0, 0.1),
    0 5px 15px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  
  transition: all 0.3s ease;
}

.zl-message-item:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 25px 70px rgba(0, 0, 0, 0.2),
    0 15px 40px rgba(0, 0, 0, 0.15),
    0 8px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.message-content {
  display: flex;
  align-items: flex-start;
  flex: 1;
  gap: 12px;
}

.message-icon {
  font-size: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}

.message-text {
  flex: 1;
  min-width: 0;
}

.message-title {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
  margin-bottom: 4px;
}

.message-description {
  font-size: 13px;
  color: #7f8c8d;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 12px;
  cursor: pointer;
  color: #95a5a6;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.message-close:hover {
  color: #2c3e50;
  background: rgba(255, 255, 255, 0.3);
}

/* 不同类型的消息样式 */
.zl-message-success {
  border-left: 4px solid #52c41a;
}

.zl-message-error {
  border-left: 4px solid #ff4d4f;
}

.zl-message-warning {
  border-left: 4px solid #faad14;
}

.zl-message-info {
  border-left: 4px solid #1890ff;
}

/* 动画效果 */
.message-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.message-leave-active {
  transition: all 0.3s ease-in;
}

.message-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.message-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.message-move {
  transition: transform 0.3s ease;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .zl-message-container {
    top: 10px;
    right: 10px;
    left: 10px;
  }
  
  .zl-message-item {
    min-width: auto;
    max-width: none;
    margin-bottom: 8px;
    padding: 12px 16px;
  }
  
  .message-title {
    font-size: 13px;
  }
  
  .message-description {
    font-size: 12px;
  }
}
</style>